import { useEffect, useState } from "react";
import { Users, ShareNetwork } from "@phosphor-icons/react";
import api from "../lib/api";

/**
 * Compact growth-signals card for the owner dashboard. Shows two
 * lightweight metrics side-by-side:
 *   • Total unique followers across every truck the owner has
 *     (one person following 2 trucks counts once)
 *   • Share views in the last 30 days — every time someone tapped Share
 *     and the recipient (or the iMessage crawler fetching the OG card)
 *     loaded the truck's share landing page.
 *
 * Non-blocking, silently skips render if the owner has no trucks yet.
 */
export default function FollowerCountCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/owner/follower-counts")
      .then(({ data }) => {
        if (mounted) setData(data);
      })
      .catch(() => {
        if (mounted) setData(null);
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  if (loading || !data) return null;
  const followers = data.total_followers || 0;
  const shares30 = data.shares_30d || 0;

  return (
    <div
      className="rounded-2xl border border-border bg-card p-5"
      data-testid="followers-card"
    >
      <div className="grid grid-cols-2 divide-x divide-border">
        {/* Followers cell */}
        <div className="flex items-center gap-3 pr-4">
          <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-none">
            <Users size={20} weight="duotone" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Followers
            </div>
            <div className="flex items-baseline gap-1.5">
              <div
                className="font-display font-bold text-2xl tracking-tight"
                data-testid="followers-total"
              >
                {followers}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {followers === 1 ? "person" : "people"}
              </div>
            </div>
          </div>
        </div>
        {/* Shares cell */}
        <div className="flex items-center gap-3 pl-4">
          <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-none">
            <ShareNetwork size={20} weight="duotone" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              Shares · 30d
            </div>
            <div className="flex items-baseline gap-1.5">
              <div
                className="font-display font-bold text-2xl tracking-tight"
                data-testid="shares-30d"
              >
                {shares30}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {shares30 === 1 ? "view" : "views"}
              </div>
            </div>
          </div>
        </div>
      </div>
      {followers === 0 && shares30 === 0 && (
        <div className="text-xs text-muted-foreground mt-3 leading-relaxed">
          No traction yet. Tap <span className="font-semibold text-foreground">Share</span> on
          your truck card and post the link to your social — every share
          shows up here.
        </div>
      )}
    </div>
  );
}
